// Modal that shows extracted incomes + expenses for review before they are
// applied to the budget. Lets the user uncheck, recategorize, or edit amounts.
import React, { useEffect, useMemo, useState } from "react";
import { CheckCircle2, X, AlertTriangle, Sparkles, Loader2, TrendingUp } from "lucide-react";
import { CATEGORY_COLORS, fmtCAD } from "../lib/budget";

const stableKey = (prefix, item, idx) =>
  `${prefix}-${idx}-${item.description}-${item.amount}`;

export default function ReviewModal({
  open,
  result,
  categories,
  currentIncome,
  onClose,
  onApply,
}) {
  const [expenseRows, setExpenseRows] = useState([]);
  const [incomeRows, setIncomeRows] = useState([]);
  const [investmentRows, setInvestmentRows] = useState([]);
  const [replaceMonth, setReplaceMonth] = useState(true);
  const [updateIncome, setUpdateIncome] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!result) return;
    setExpenseRows(
      (result.expenses || []).map((e, i) => ({
        ...e, included: true, _k: stableKey("e", e, i),
      }))
    );
    setIncomeRows(
      (result.incomes || []).map((it, i) => ({
        ...it, included: true, _k: stableKey("i", it, i),
      }))
    );
    setInvestmentRows(
      (result.investments || []).map((it, i) => ({
        ...it, included: true, _k: stableKey("v", it, i),
      }))
    );
    setReplaceMonth(true);
    setUpdateIncome(true);
  }, [result]);

  const totals = useMemo(() => {
    const t = {};
    (categories || []).forEach((c) => { t[c] = 0; });
    let totalExp = 0;
    expenseRows.forEach((r) => {
      if (!r.included) return;
      const cat = t[r.category] !== undefined ? r.category : "Miscellaneous";
      const amt = Number(r.amount) || 0;
      t[cat] = Math.round((t[cat] + amt) * 100) / 100;
      totalExp += amt;
    });
    let totalInc = 0;
    incomeRows.forEach((r) => {
      if (!r.included) return;
      totalInc += Number(r.amount) || 0;
    });
    let totalInv = 0;
    investmentRows.forEach((r) => {
      if (!r.included) return;
      totalInv += Number(r.amount) || 0;
    });
    return {
      perCategory: t,
      totalExp: Math.round(totalExp * 100) / 100,
      totalInc: Math.round(totalInc * 100) / 100,
      totalInv: Math.round(totalInv * 100) / 100,
    };
  }, [expenseRows, incomeRows, investmentRows, categories]);

  if (!open || !result) return null;

  const updateExpense = (k, patch) => setExpenseRows((rows) => rows.map((r) => (r._k === k ? { ...r, ...patch } : r)));
  const updateIncomeRow = (k, patch) => setIncomeRows((rows) => rows.map((r) => (r._k === k ? { ...r, ...patch } : r)));
  const updateInvestment = (k, patch) => setInvestmentRows((rows) => rows.map((r) => (r._k === k ? { ...r, ...patch } : r)));

  const handleApply = async () => {
    setBusy(true);
    try {
      // Only include categories that actually had at least one extracted transaction.
      // This prevents the "Replace this month's values" toggle from accidentally
      // zeroing out categories the statement doesn't cover (e.g. uploading a debit
      // statement when Rent / Car Loan are paid from a different account).
      const touchedCats = new Set();
      expenseRows.forEach((r) => { if (r.included) touchedCats.add(r.category || "Miscellaneous"); });
      const perCat = {};
      Object.entries(totals.perCategory).forEach(([c, v]) => {
        if (touchedCats.has(c)) perCat[c] = v;
      });

      const includedIncomes = incomeRows.filter((r) => r.included);
      const mainTotal = includedIncomes.filter((r) => (r.kind || "income") !== "refund").reduce((s, r) => s + (Number(r.amount) || 0), 0);
      const refundTotal = includedIncomes.filter((r) => r.kind === "refund").reduce((s, r) => s + (Number(r.amount) || 0), 0);
      const investmentTotal = investmentRows.filter((r) => r.included).reduce((s, r) => s + (Number(r.amount) || 0), 0);
      await onApply({
        replaceMonth,
        updateIncome,
        incomeDelta: updateIncome ? Math.round(mainTotal * 100) / 100 : 0,
        refundsDelta: updateIncome ? Math.round(refundTotal * 100) / 100 : 0,
        investmentsDelta: Math.round(investmentTotal * 100) / 100,
        expenseTotals: perCat,
        touchedExpenseCategories: Array.from(touchedCats),
        hasIncomeData: includedIncomes.some((r) => (r.kind || "income") !== "refund"),
        hasRefundsData: includedIncomes.some((r) => r.kind === "refund"),
        hasInvestmentsData: investmentRows.some((r) => r.included),
      });
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="review-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#1A1C1A]/40 backdrop-blur-sm p-4 overflow-y-auto"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white w-full max-w-5xl rounded-2xl border border-[#D8D6D0] shadow-2xl my-8 max-h-[90vh] flex flex-col">
        <Header
          incomeCount={result.income_count || 0}
          expenseCount={result.expense_count || 0}
          investmentCount={result.investment_count || 0}
          skippedCount={(result.skipped || []).length}
          onClose={onClose}
        />

        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-6">
          {result.truncated && (
            <div className="text-xs px-3 py-2 rounded-lg bg-[#FFF4E5] border border-[#F4D9A7] text-[#8A5A1A]">
              Heads up: this statement was very long and was truncated for processing. Some later transactions may not appear — try uploading one month at a time for best results.
            </div>
          )}

          {expenseRows.length === 0 && (
            <div data-testid="review-no-expenses-warn" className="text-xs px-3 py-2 rounded-lg bg-[#FCEDE6] border border-[#F0C6B5] text-[#8A4030]">
              ⚠️ We couldn&apos;t find any expenses in this statement. Applying will not change your expense rows. Try uploading a clearer file or a CSV.
            </div>
          )}

          <IncomeSection
            rows={incomeRows}
            onChange={updateIncomeRow}
            currentIncome={currentIncome}
            updateIncome={updateIncome}
            setUpdateIncome={setUpdateIncome}
            total={totals.totalInc}
          />

          {investmentRows.length > 0 && (
            <InvestmentSection
              rows={investmentRows}
              onChange={updateInvestment}
              total={totals.totalInv}
            />
          )}

          <ExpenseSection
            rows={expenseRows}
            onChange={updateExpense}
            categories={categories}
            perCategory={totals.perCategory}
            total={totals.totalExp}
          />

          {(result.skipped || []).length > 0 && <SkippedSection skipped={result.skipped} />}
        </div>

        <Footer
          replaceMonth={replaceMonth}
          setReplaceMonth={setReplaceMonth}
          busy={busy}
          onCancel={onClose}
          onApply={handleApply}
          expenseTotal={totals.totalExp}
          incomeTotal={totals.totalInc}
          investmentTotal={totals.totalInv}
          updateIncome={updateIncome}
        />
      </div>
    </div>
  );
}

// ============ Sub-components ============
const Header = ({ incomeCount, expenseCount, investmentCount, skippedCount, onClose }) => (
  <div className="flex items-center justify-between px-6 py-4 border-b border-[#E8E6E1]">
    <div>
      <h2 className="font-display text-xl font-semibold flex items-center gap-2">
        <Sparkles size={18} className="text-[#5F7161]" />
        Review extracted statement
      </h2>
      <p className="text-xs text-[#5A5C58] mt-1">
        {incomeCount} income · {investmentCount} investment{investmentCount === 1 ? "" : "s"} · {expenseCount} expenses · {skippedCount} skipped. Edit anything below, then apply.
      </p>
    </div>
    <button
      data-testid="review-close-btn"
      onClick={onClose}
      className="p-2 rounded-full hover:bg-[#F4F2EC] transition-colors"
      aria-label="Close review"
    >
      <X size={18} />
    </button>
  </div>
);

const IncomeSection = ({ rows, onChange, currentIncome, updateIncome, setUpdateIncome, total }) => (
  <section data-testid="review-income">
    <div className="flex items-center justify-between mb-3">
      <h3 className="font-display font-semibold text-base">Income detected</h3>
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input
          data-testid="review-update-income"
          type="checkbox"
          checked={updateIncome}
          onChange={(e) => setUpdateIncome(e.target.checked)}
          className="accent-[#5F7161]"
        />
        <span>Add to <span className="font-mono">{fmtCAD(currentIncome)}</span> income field</span>
      </label>
    </div>
    {rows.length === 0 ? (
      <p className="text-sm text-[#5A5C58] italic bg-[#F4F2EC] rounded-lg px-3 py-2">No income transactions detected.</p>
    ) : (
      <div className="rounded-xl border border-[#E8E6E1] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F4F2EC] text-[#5A5C58] text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-2 w-10"></th>
              <th className="text-left px-3 py-2 w-24">Date</th>
              <th className="text-left px-3 py-2">Description</th>
              <th className="text-right px-3 py-2 w-32">Amount (CAD)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r._k} className="border-t border-[#E8E6E1]">
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    checked={r.included}
                    onChange={(e) => onChange(r._k, { included: e.target.checked })}
                    className="accent-[#5F7161]"
                    data-testid={`review-income-include-${r._k}`}
                  />
                </td>
                <td className="px-3 py-2 font-mono text-xs text-[#5A5C58]">{r.date || "—"}</td>
                <td className="px-3 py-2 max-w-[320px]">
                  <div className="flex items-center gap-2">
                    <span className="truncate">{r.description}</span>
                    {r.kind === "refund" && (
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#5F716122] text-[#5F7161] shrink-0">
                        Refund
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 text-right">
                  <input
                    type="number"
                    value={r.amount}
                    step="0.01"
                    onChange={(e) => onChange(r._k, { amount: Number(e.target.value) || 0 })}
                    className="w-28 text-right font-mono bg-white border border-[#D8D6D0] rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[#5F7161]/40"
                  />
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-[#F9F8F6] font-medium">
              <td colSpan="3" className="px-3 py-2 text-right text-[#5A5C58] text-xs uppercase tracking-wider">Included total</td>
              <td className="px-3 py-2 text-right font-mono">{fmtCAD(total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    )}
  </section>
);

const ExpenseSection = ({ rows, onChange, categories, perCategory, total }) => (
  <section data-testid="review-expenses">
    <div className="flex items-center justify-between mb-3">
      <h3 className="font-display font-semibold text-base">Expenses detected</h3>
      <span className="text-xs text-[#5A5C58]">{rows.filter((r) => r.included).length} of {rows.length} included</span>
    </div>
    <div className="rounded-xl border border-[#E8E6E1] overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-[#F4F2EC] text-[#5A5C58] text-xs uppercase tracking-wider">
          <tr>
            <th className="text-left px-3 py-2 w-10"></th>
            <th className="text-left px-3 py-2 w-24">Date</th>
            <th className="text-left px-3 py-2">Description</th>
            <th className="text-left px-3 py-2 w-44">Category</th>
            <th className="text-right px-3 py-2 w-32">Amount</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r._k} className={`border-t border-[#E8E6E1] ${!r.included ? "opacity-50" : ""}`}>
              <td className="px-3 py-2">
                <input
                  type="checkbox"
                  checked={r.included}
                  onChange={(e) => onChange(r._k, { included: e.target.checked })}
                  className="accent-[#5F7161]"
                  data-testid={`review-expense-include-${r._k}`}
                />
              </td>
              <td className="px-3 py-2 font-mono text-xs text-[#5A5C58]">{r.date || "—"}</td>
              <td className="px-3 py-2 truncate max-w-[280px]">{r.description}</td>
              <td className="px-3 py-2">
                <CategorySelect
                  value={r.category}
                  categories={categories}
                  onChange={(v) => onChange(r._k, { category: v })}
                  testId={`review-expense-cat-${r._k}`}
                />
              </td>
              <td className="px-3 py-2 text-right">
                <input
                  type="number"
                  step="0.01"
                  value={r.amount}
                  onChange={(e) => onChange(r._k, { amount: Number(e.target.value) || 0 })}
                  className="w-28 text-right font-mono bg-white border border-[#D8D6D0] rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[#5F7161]/40"
                />
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-[#F9F8F6] font-medium">
            <td colSpan="4" className="px-3 py-2 text-right text-[#5A5C58] text-xs uppercase tracking-wider">Total expenses</td>
            <td className="px-3 py-2 text-right font-mono">{fmtCAD(total)}</td>
          </tr>
        </tfoot>
      </table>
    </div>

    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 mt-4">
      {Object.entries(perCategory)
        .filter(([, v]) => v !== 0)
        .map(([c, v]) => (
          <div key={c} className="bg-[#F4F6F1] border border-[#E0E5DA] rounded-lg px-3 py-2">
            <div className="flex items-center gap-1.5 text-[11px] text-[#5A5C58]">
              <span className="w-2 h-2 rounded-full" style={{ background: CATEGORY_COLORS[c] }} />
              {c}
            </div>
            <p className="font-mono text-sm font-medium text-[#1A1C1A]">{fmtCAD(v)}</p>
          </div>
        ))}
    </div>
  </section>
);

const CategorySelect = ({ value, categories, onChange, testId }) => (
  <select
    data-testid={testId}
    value={value || "Miscellaneous"}
    onChange={(e) => onChange(e.target.value)}
    className="w-full bg-white border border-[#D8D6D0] rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-[#5F7161]/40"
  >
    {(categories || []).map((c) => (
      <option key={c} value={c}>{c}</option>
    ))}
  </select>
);

const SkippedSection = ({ skipped }) => (
  <section data-testid="review-skipped">
    <h3 className="font-display font-semibold text-base mb-2 flex items-center gap-2 text-[#5A5C58]">
      <AlertTriangle size={14} /> Skipped lines ({skipped.length})
    </h3>
    <p className="text-xs text-[#5A5C58] mb-2">
      These rows were intentionally ignored (transfers between your own accounts, balances, headers).
    </p>
    <div className="rounded-xl bg-[#F9F8F6] border border-[#E8E6E1] px-4 py-3 max-h-32 overflow-y-auto text-xs space-y-1">
      {skipped.map((s, i) => (
        <div key={`${s.description}-${i}`} className="flex justify-between gap-3">
          <span className="truncate text-[#5A5C58]">{s.description}</span>
          <span className="font-mono uppercase tracking-wider text-[#8A8C88] shrink-0">{s.reason}</span>
        </div>
      ))}
    </div>
  </section>
);

const Footer = ({ replaceMonth, setReplaceMonth, busy, onCancel, onApply, expenseTotal, incomeTotal, investmentTotal, updateIncome }) => (
  <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 border-t border-[#E8E6E1] bg-[#F9F8F6]">
    <label className="flex items-center gap-2 text-sm cursor-pointer">
      <input
        data-testid="review-replace-month"
        type="checkbox"
        checked={replaceMonth}
        onChange={(e) => setReplaceMonth(e.target.checked)}
        className="accent-[#5F7161]"
      />
      <span>Replace this month&apos;s values <span className="text-[#8A8C88]">(unchecked = add to existing)</span></span>
    </label>
    <div className="flex items-center gap-3">
      <div className="text-right text-xs text-[#5A5C58] hidden sm:block">
        <p className="font-mono"><span className="text-[#5F7161]">+{fmtCAD(updateIncome ? incomeTotal : 0)}</span> income</p>
        {investmentTotal > 0 && (
          <p className="font-mono"><span className="text-[#5F7161]">+{fmtCAD(investmentTotal)}</span> investments</p>
        )}
        <p className="font-mono"><span className="text-[#B8593E]">−{fmtCAD(expenseTotal)}</span> expenses</p>
      </div>
      <button
        data-testid="review-cancel-btn"
        onClick={onCancel}
        className="px-4 py-2 rounded-full text-[#5A5C58] hover:bg-[#E8E6E1]/60 text-sm font-medium transition-colors"
      >
        Cancel
      </button>
      <button
        data-testid="review-apply-btn"
        onClick={onApply}
        disabled={busy}
        className="flex items-center gap-2 bg-[#5F7161] hover:bg-[#4E5C50] disabled:opacity-60 text-white rounded-full px-5 py-2 text-sm font-medium transition-colors"
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
        Apply to budget
      </button>
    </div>
  </div>
);

const InvestmentSection = ({ rows, onChange, total }) => (
  <section data-testid="review-investments">
    <div className="flex items-center justify-between mb-3">
      <h3 className="font-display font-semibold text-base flex items-center gap-2">
        <TrendingUp size={16} className="text-[#5F7161]" /> Investments detected
      </h3>
      <span className="text-xs text-[#5A5C58]">
        These contribute to your <span className="font-medium">Investments</span> field — not expenses.
      </span>
    </div>
    <div className="rounded-xl border border-[#E8E6E1] overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-[#F4F2EC] text-[#5A5C58] text-xs uppercase tracking-wider">
          <tr>
            <th className="text-left px-3 py-2 w-10"></th>
            <th className="text-left px-3 py-2 w-24">Date</th>
            <th className="text-left px-3 py-2">Description</th>
            <th className="text-right px-3 py-2 w-32">Amount (CAD)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r._k} className={`border-t border-[#E8E6E1] ${!r.included ? "opacity-50" : ""}`}>
              <td className="px-3 py-2">
                <input
                  type="checkbox"
                  checked={r.included}
                  onChange={(e) => onChange(r._k, { included: e.target.checked })}
                  className="accent-[#5F7161]"
                  data-testid={`review-investment-include-${r._k}`}
                />
              </td>
              <td className="px-3 py-2 font-mono text-xs text-[#5A5C58]">{r.date || "—"}</td>
              <td className="px-3 py-2 max-w-[320px]">
                <div className="flex items-center gap-2">
                  <span className="truncate">{r.description}</span>
                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#3E6B9D22] text-[#3E6B9D] shrink-0">
                    Investment
                  </span>
                </div>
              </td>
              <td className="px-3 py-2 text-right">
                <input
                  type="number"
                  value={r.amount}
                  step="0.01"
                  onChange={(e) => onChange(r._k, { amount: Number(e.target.value) || 0 })}
                  className="w-28 text-right font-mono bg-white border border-[#D8D6D0] rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[#5F7161]/40"
                />
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-[#F9F8F6] font-medium">
            <td colSpan="3" className="px-3 py-2 text-right text-[#5A5C58] text-xs uppercase tracking-wider">Goes to Investments field</td>
            <td className="px-3 py-2 text-right font-mono">{fmtCAD(total)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  </section>
);
